const API_URL = process.env.REACT_APP_API_URL || '/api';

async function request(endpoint, options = {}) {
  const token = localStorage.getItem('crunchy_token');
  const headers = { ...options.headers };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Request failed');
  }
  return data;
}

// Auth
export const authAPI = {
  register: (body) => request('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body) => request('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  forgotPassword: (email) => request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, password) => request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  changePassword: (currentPassword, newPassword) => request('/auth/me/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  getMe: () => request('/auth/me'),
  updateProfile: (body) => request('/auth/me', { method: 'PATCH', body: JSON.stringify(body) }),
  uploadAvatar: (formData) => request('/auth/me/avatar', { method: 'POST', body: formData }),
  updatePreferences: (body) => request('/auth/me/preferences', { method: 'PUT', body: JSON.stringify(body) }),
  completeOnboarding: (body) => request('/auth/me/onboarding', { method: 'POST', body: JSON.stringify(body) }),
  checkUsername: (username) => request(`/auth/check-username/${encodeURIComponent(username)}`),
  getUser: (id) => request(`/auth/${id}`),
  searchUsers: (params) => request(`/auth?${new URLSearchParams(params)}`),
  toggleWatch: (id) => request(`/auth/${id}/watch`, { method: 'POST' }),
  getWatching: () => request('/auth/me/watching'),
};

// Recipes
export const recipesAPI = {
  review: (body) => request('/recipes/review', { method: 'POST', body: JSON.stringify(body) }),
  create: (formData) => request('/recipes', { method: 'POST', body: formData }),
  getAll: (params) => request(`/recipes?${new URLSearchParams(params)}`),
  getOne: (id) => request(`/recipes/${id}`),
  update: (id, body) => request(`/recipes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id) => request(`/recipes/${id}`, { method: 'DELETE' }),
  uploadImages: (id, formData) => request(`/recipes/${id}/images`, { method: 'POST', body: formData }),
  deleteImage: (id, imageId) => request(`/recipes/${id}/images/${imageId}`, { method: 'DELETE' }),
  addComment: (id, message) => request(`/recipes/${id}/comments`, { method: 'POST', body: JSON.stringify({ message }) }),
  deleteComment: (id, commentId) => request(`/recipes/${id}/comments/${commentId}`, { method: 'DELETE' }),
  toggleFavorite: (id) => request(`/recipes/${id}/add`, { method: 'POST' }),
  getRating: (id) => request(`/recipes/${id}/rating`),
  getMyRating: (id) => request(`/recipes/${id}/rating/me`),
  rateRecipe: (id, score) => request(`/recipes/${id}/rating`, { method: 'POST', body: JSON.stringify({ score }) }),
  getCollection: () => request('/recipes/collection/me'),
  getFollowingFeed: (params) => request(`/recipes/following/feed?${new URLSearchParams(params)}`),
};

// Messages
export const messagesAPI = {
  getConversations: () => request('/messages/conversations'),
  getOrCreateConversation: (userId) => request('/messages/conversations', { method: 'POST', body: JSON.stringify({ userId }) }),
  getMessages: (convId, params) => request(`/messages/conversations/${convId}/messages?${new URLSearchParams(params)}`),
  sendMessage: (convId, text) => request(`/messages/conversations/${convId}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),
  getUnreadCount: () => request('/messages/unread'),
};

// Admin
export const adminAPI = {
  getStats: () => request('/admin/stats'),
  getUsers: (params) => request(`/admin/users?${new URLSearchParams(params)}`),
  deleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  getUserInfo: (id) => request(`/admin/users/${id}/info`),
  updateRole: (id, role) => request(`/admin/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  toggleDisable: (id) => request(`/admin/users/${id}/disable`, { method: 'PATCH' }),
  resetPassword: (id) => request(`/admin/users/${id}/reset-password`, { method: 'POST' }),
  regenerateUserPhoto: (id) => request(`/admin/users/${id}/regenerate-photo`, { method: 'POST' }),
  getRecipes: (params) => request(`/admin/recipes?${new URLSearchParams(params)}`),
  deleteRecipe: (id) => request(`/admin/recipes/${id}`, { method: 'DELETE' }),
  deleteComment: (recipeId, commentId) => request(`/admin/recipes/${recipeId}/comments/${commentId}`, { method: 'DELETE' }),
  generateRecipe: () => request('/admin/generate-recipe', { method: 'POST' }),
  regeneratePhoto: (id) => request(`/admin/recipes/${id}/regenerate-photo`, { method: 'POST' }),
  getReviewStats: (params) => request(`/admin/review-stats?${new URLSearchParams(params)}`),
};
